import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import ContentLoader from '../../components/ui/ContentLoader';
import ProductForm from '../../components/products/ProductForm';
import { getProduct, createProduct, updateProduct } from '../../services/api';
import { Product, ProductFormData } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

const ProductOnboardingPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canManageProducts } = usePermissions();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Redirect to products page - use the modal instead
  useEffect(() => {
    navigate('/products');
  }, [navigate]);
  
  const isEditMode = !!productId;
  
  // Check permissions
  useEffect(() => {
    if (!canManageProducts()) {
      toast.error('You do not have permission to manage products');
      navigate('/products');
    }
  }, [canManageProducts, navigate]);
  
  // Fetch product if editing
  useEffect(() => {
    if (isEditMode) {
      fetchProduct(productId);
    }
  }, [isEditMode, productId]);
  
  // Fetch product data from API
  const fetchProduct = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getProduct(id);
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product data');
      setError('Failed to load product data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (formData: ProductFormData) => {
    setIsSubmitting(true);
    
    // Debug logging to check the structure
    // Product form data prepared for submission
    
    try {
      // Make sure all integration fields are properly formatted, especially for multi-select
      formData.integration_fields = formData.integration_fields.map(field => {
        const formatted = { ...field };
        
        // Ensure options are included for multi-select fields
        if (field.type === 'multi-select' && !formatted.options) {
          formatted.options = [];
        }
        
        return formatted;
      });
      
      if (isEditMode) {
        // Update existing product
        await updateProduct(productId as string, formData);
        toast.success('Product updated successfully');
      } else {
        // Create new product
        await createProduct(formData);
        toast.success('Product created successfully');
      }
      
      // Navigate back after successful save
      navigate('/products');
    } catch (error: any) {
      console.error('Error saving product:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to save product');
      }
      
      setIsSubmitting(false);
      setError('Failed to save product');
    }
  };
  
  // Just return a loading indicator since we're redirecting
  return (
    <div className="p-6">
      <ContentLoader
        message="Redirecting to products page..."
        size="lg"
      />
    </div>
  );
};

export default ProductOnboardingPage; 